import './styles.modules.scss';
import qs from 'querystring';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import ProductCartForm from '../../ProductCartForm';
import { Product } from '~domain/product/Product';
import { Product as TProduct, ProductType } from '~redux/brand/types';
import { formatCurrency } from '~utils/formatter';
import { getPublicImageUrl } from '~utils/image';
import ProductDetailModal from '~view/widgets/ProductDetailModal';

type ProductDetailViewItemProps = {
  product: TProduct;
  productTypes?: ProductType[];
  index: number;
  preview?: boolean;
};

const ProductDetailViewItem: React.FC<ProductDetailViewItemProps> = ({
  product: productJson,
  productTypes,
  preview,
}) => {
  const product = useMemo(() => Product.create(productJson), [productJson]);
  const selectColor = useCallback(
    (color) => {
      const productType = productTypes?.find((type) => type.color === color);
      if (!productType?.imageKey) {
        return;
      }
    },
    [productTypes]
  );

  const selectSize = useCallback(
    (size) => {
      const productType = productTypes?.find((type) => type.size === size);
      if (!productType?.imageKey) {
        return;
      }
    },
    [productTypes]
  );

  const selectType = useCallback((type) => {
    const productType = productTypes?.find((t) => t.type_name === type);
    if (!productType?.imageKey) {
      return;
    }
  }, []);

  const is_direct = !!qs.parse(location.search?.split('?')?.[1])?.direct;

  const [image, setImage] = useState<string>();

  const [isShowDetail, setIsShowDetail] = useState(false);

  return (
    <Row>
      <Col
        md={12}
        lg={6}
        className="flex-shrink-0 mb-3 text-center text-lg-left"
      >
        <Link to={`/productdetail/${encodeURIComponent(product.id)}`}>
          <div
            className="mb-4"
            style={{ width: '100%', paddingTop: '100%', position: 'relative' }}
          >
            <img
              src={getPublicImageUrl(
                image ?? product.imageKeys?.[0]?.imageKey ?? '',
                'large'
              )}
              onError={(e) => {
                e.currentTarget.src =
                  'https://placehold.jp/500x500.png?text=No image';
              }}
              className="border bg-black-50"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              loading="lazy"
              alt={product.product_name}
            />
          </div>
        </Link>
        <div className="d-flex flex-wrap">
          {product.imageKeys?.map(({ imageKey }) => (
            <div
              className={
                'mr-2 mb-2' + (imageKey === image ? ' border border-dark' : '')
              }
              styleName="detail-view-item-thumbnails"
              onClick={() => setImage(imageKey)}
            >
              <img
                src={getPublicImageUrl(imageKey, 'large')}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                alt=""
                onError={(e) => {
                  e.currentTarget.src =
                    'https://placehold.jp/60x60.png?text=No image';
                }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
        {/* <div styleName="product-description">{product.product_content}</div> */}
      </Col>
      <Col md={12} lg={6} className="text-left mb-3">
        <Link to={`/productdetail/${encodeURIComponent(product.id)}`}>
          <h3>{product.product_name}</h3>
        </Link>
        <p>
          <h5 className="d-inline m-0">{formatCurrency(product.unitPrice)}</h5>
          （
          <span>
            希望小売価格：
            {product.is_open_price
              ? 'オープン価格'
              : formatCurrency(product.product_retail_price)}
          </span>
          {!product.is_open_price && (
            <span>
              、 掛け率：
              {product.productSaleWholesaleRate ? (
                <>
                  <span className="emphasis">
                    {product.productSaleWholesaleRate}％
                  </span>
                  &nbsp;
                  <del>{product.productWholesaleRate}％</del>
                </>
              ) : (
                <>{product.productWholesaleRate}％</>
              )}
            </span>
          )}
          ）
        </p>
        {product.product_preorder != 1 ? (
          <p>
            <strong>発送予定日</strong>：
            {product.product_estimated_ship_date_min}〜
            {product.product_estimated_ship_date_max}
            営業日で発送予定
          </p>
        ) : (
          <p>
            <strong>発送予定日</strong>：
            {product.product_preorder_shipping_date}
            に発送予定
          </p>
        )}
        <p>
          <strong>品番</strong>：{product.product_number}
        </p>
        <p>
          <a
            className="font-weight-bold"
            style={{ textDecoration: 'underline' }}
            onClick={() => setIsShowDetail(true)}
          >
            詳細を表示
          </a>
        </p>
        {isShowDetail && (
          <ProductDetailModal
            product={product}
            isOpen
            toggle={() => setIsShowDetail(false)}
          />
        )}
        <ProductCartForm
          product={product}
          productTypes={productTypes}
          onSelectColor={selectColor}
          onSelectSize={selectSize}
          onSelectType={selectType}
          isDirect={is_direct}
          preview={preview}
        />
      </Col>
    </Row>
  );
};

export default ProductDetailViewItem;
